import React from 'react';
import { useIntl } from 'react-intl';
import MaterialTable, { MaterialTableProps } from '@material-table/core';
import { TablePagination } from '@mui/material';

type Props<T extends object> = MaterialTableProps<T> & {
  page?: number;
  pageSize?: number;
  count?: number;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (pageSize: number) => void;
};

const LocalizedMaterialTable = <T extends object>({
  data,
  title,
  actions,
  columns,
  options,
  components,
  parentChildData,
  page,
  pageSize,
  count,
  onPageChange,
  onRowsPerPageChange,
}: Props<T>) => {
  const intl = useIntl();

  const isControlled = page !== undefined && pageSize !== undefined && count !== undefined;

  const localization = {
    body: {
      emptyDataSourceMessage: intl.formatMessage({ id: 'tables.no.records' }),
    },
    pagination: {
      labelRowsPerPage: intl.formatMessage({ id: 'tables.rows.per.page' }),
      firstTooltip: intl.formatMessage({ id: 'tables.first.page.tooltip' }),
      previousTooltip: intl.formatMessage({ id: 'tables.previous.page.tooltip' }),
      nextTooltip: intl.formatMessage({ id: 'tables.next.page.tooltip' }),
      lastTooltip: intl.formatMessage({ id: 'tables.last.page.tooltip' }),
    },
    toolbar: {
      searchTooltip: intl.formatMessage({ id: 'tables.search' }),
      searchPlaceholder: intl.formatMessage({ id: 'tables.search' }),
    },
  } as any;

  const normalize = (value: string) => value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
  const getColumnNestedValue = (object: T, path: string) => {
    return path.split('.').reduce<any>((value, key) => { return value?.[key] }, object);
  };

  const localizedColumns = columns.map((column) => {
    // keep custom sorting of column
    if (column.customSort || typeof column.field !== 'string') return column;

    return {
      ...column,
      customSort: (a: T, b: T) => {
        const aValue = getColumnNestedValue(a, column.field as string);
        const bValue = getColumnNestedValue(b, column.field as string);

        // empty/null/undefined values
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return -1;
        if (bValue == null) return 1;

        // case-insensitive and accent-insensitive sorting for strings
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return normalize(aValue).localeCompare(normalize(bValue),
            undefined,
            { sensitivity: 'base' }
          );
        }
        if (typeof aValue === 'number' && typeof bValue === 'number') return aValue - bValue;

        return String(aValue).localeCompare(String(bValue), undefined, { sensitivity: 'base' });
      },
    };
  });

  return (
    <React.Fragment>
      <MaterialTable
        key={`table-${pageSize}`}
        title={title}
        columns={localizedColumns}
        data={data}
        actions={actions}
        parentChildData={parentChildData}
        localization={localization}
        options={{
          ...options,
          pageSize: pageSize ?? options?.pageSize ?? 10,
          paging: true,
        }}
        components={{
          ...components,
          Pagination: (props) => {
            const safePage = isControlled ? page! : props.page ?? 0;
            const safeRowsPerPage = isControlled
              ? pageSize!
              : props.rowsPerPage ?? 10;
            const safeCount = isControlled ? count! : props.count ?? 0;

            return (
              <TablePagination
                {...props}
                count={safeCount}
                page={safePage}
                rowsPerPage={safeRowsPerPage}
                onPageChange={(event, newPage) => {
                  if (onPageChange) {
                    onPageChange(newPage);
                  } else {
                    props.onPageChange(event, newPage);
                  }
                }}
                onRowsPerPageChange={(event) => {
                  const size = Number(event.target.value);

                  if (onRowsPerPageChange) {
                    onRowsPerPageChange(size);
                  } else {
                    props.onRowsPerPageChange(event);
                  }
                }}
                labelRowsPerPage={intl.formatMessage({
                  id: 'tables.rows.per.page',
                })}
                labelDisplayedRows={({ from, to, count }) => {
                  const safeFrom = from ?? 0;
                  const safeTo = to ?? 0;
                  const safeCount = count ?? 0;

                  return intl.formatMessage(
                    { id: 'tables.displayed.rows' },
                    {
                      from: safeFrom,
                      to: safeTo,
                      count:
                        safeCount !== -1
                          ? safeCount
                          : `more than ${safeTo}`,
                    }
                  );
                }}
                SelectProps={{
                  ...props.SelectProps,
                  renderValue: (value) => value, //shows "10" instead of "10 rows"
                }}
              />
            );
          },
        }}
      />
    </React.Fragment>
  );
};

export default LocalizedMaterialTable;